import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, Copy, Loader2 } from "lucide-react";
import type { BillingConfig, CancelSubscriptionMode, InstanceSubscription } from "@/types/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMoney } from "@/lib/billing-format";
import { useSubscriptionOps } from "./use-billing-ops";

const LIVE = ["active", "past_due", "paused", "pending_mandate"];
const ENDED = ["none", "canceled", "failed"];

interface Props {
  name: string;
  subscription: InstanceSubscription | null;
  config: BillingConfig | null;
}

/**
 * Operator subscription actions against Stripe. Only the actions that are
 * valid for the current subscription status are offered; the backend
 * enforces the same rules.
 */
export function SubscriptionActionsCard({ name, subscription, config }: Props) {
  const { t } = useTranslation("billing");
  const ops = useSubscriptionOps(name);
  const [dialog, setDialog] = React.useState<"subscribe" | "cancel" | null>(null);

  const status = subscription?.status ?? "none";
  const hasStripeSub = Boolean(subscription?.stripeSubscriptionId);
  const cancelAtPeriodEnd = Boolean(subscription?.cancelAtPeriodEnd);
  const canSubscribe =
    Boolean(config?.isActive) &&
    (ENDED.includes(status) || (status === "pending_mandate" && !hasStripeSub));
  const canCancel = hasStripeSub && LIVE.includes(status) && !cancelAtPeriodEnd;
  const canResume = hasStripeSub && cancelAtPeriodEnd && status !== "canceled";
  const hasCustomer = Boolean(subscription?.stripeCustomerId);
  const portalUrl = ops.portalLink.data?.url ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("ops.card.title")}</CardTitle>
        <CardDescription>
          {t("ops.card.description", { status: t(`ops.card.status.${statusKey(status)}`) })}
          {cancelAtPeriodEnd && status !== "canceled" ? ` ${t("ops.card.endingAtPeriodEnd")}` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {canSubscribe ? (
            <Button onClick={() => setDialog("subscribe")} disabled={ops.subscribe.isPending}>
              {t("ops.subscribe.button")}
            </Button>
          ) : null}
          {canResume ? (
            <Button variant="secondary" onClick={() => ops.resume.mutate()} disabled={ops.resume.isPending}>
              {ops.resume.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.resume.button")}
            </Button>
          ) : null}
          {hasCustomer ? (
            <Button variant="outline" onClick={() => ops.portalLink.mutate()} disabled={ops.portalLink.isPending}>
              {ops.portalLink.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.portal.button")}
            </Button>
          ) : null}
          {hasCustomer ? (
            <Button variant="outline" onClick={() => ops.sync.mutate()} disabled={ops.sync.isPending}>
              {ops.sync.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.sync.button")}
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="destructive" onClick={() => setDialog("cancel")} disabled={ops.cancel.isPending}>
              {t("ops.cancel.button")}
            </Button>
          ) : null}
        </div>

        {!canSubscribe && !canCancel && !canResume && !hasCustomer ? (
          <p className="text-sm text-muted-foreground">
            {config?.isActive ? t("ops.card.nothingToDo") : t("ops.card.noConfig")}
          </p>
        ) : null}

        {portalUrl ? <PortalLinkBox url={portalUrl} /> : null}
      </CardContent>

      <SubscribeDialog
        open={dialog === "subscribe"}
        onOpenChange={(v) => setDialog(v ? "subscribe" : null)}
        config={config}
        pending={ops.subscribe.isPending}
        onConfirm={(idemKey) => ops.subscribe.mutate(idemKey, { onSuccess: () => setDialog(null) })}
      />
      <CancelDialog
        open={dialog === "cancel"}
        onOpenChange={(v) => setDialog(v ? "cancel" : null)}
        name={name}
        pending={ops.cancel.isPending}
        onConfirm={(mode) => ops.cancel.mutate(mode, { onSuccess: () => setDialog(null) })}
      />
    </Card>
  );
}

function statusKey(status: string) {
  const known = ["none", "pending_mandate", "active", "past_due", "paused", "canceled", "failed"] as const;
  return (known as readonly string[]).includes(status) ? (status as (typeof known)[number]) : "none";
}

function PortalLinkBox({ url }: { url: string }) {
  const { t } = useTranslation("billing");
  const [copied, setCopied] = React.useState(false);
  const inputId = React.useId();

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("ops.portal.copyFailed"));
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId}>{t("ops.portal.label")}</Label>
      <div className="flex gap-2">
        <Input id={inputId} readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        <Button type="button" variant="outline" onClick={copy} aria-label={t("ops.portal.copy")}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t("ops.portal.hint")}</p>
    </div>
  );
}

function SubscribeDialog({
  open,
  onOpenChange,
  config,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: BillingConfig | null;
  pending: boolean;
  onConfirm: (idemKey: string) => void;
}) {
  const { t } = useTranslation("billing");
  // One key per attempt: a double submit cannot create two subscriptions, and
  // a retry after a failure is not answered with Stripe's cached error.
  const [attempt, setAttempt] = React.useState(0);
  const idemKey = React.useMemo(() => (open ? crypto.randomUUID() : ""), [open, attempt]);
  const wasPending = React.useRef(false);
  React.useEffect(() => {
    if (wasPending.current && !pending && open) setAttempt((a) => a + 1);
    wasPending.current = pending;
  }, [pending, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent closeLabel={t("ops.close")}>
        <DialogHeader>
          <DialogTitle>{t("ops.subscribe.title")}</DialogTitle>
          <DialogDescription>{t("ops.subscribe.description")}</DialogDescription>
        </DialogHeader>
        {config ? (
          <dl className="space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="w-40 text-muted-foreground">{t("ops.subscribe.amount")}</dt>
              <dd>{formatMoney(config.amountCents, config.currency)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-40 text-muted-foreground">{t("ops.subscribe.interval")}</dt>
              <dd>
                {t("payments.subscriptions.interval", {
                  count: Number.parseInt(config.interval, 10) || 1,
                })}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-40 text-muted-foreground">{t("ops.subscribe.vat")}</dt>
              <dd>{(config.vatRateBps / 100).toFixed(2)}%</dd>
            </div>
            <div className="flex gap-2">
              <dt className="w-40 text-muted-foreground">{t("ops.subscribe.dueDays")}</dt>
              <dd>{t("ops.subscribe.days", { count: config.invoiceDueDays })}</dd>
            </div>
          </dl>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("ops.cancelAction")}
          </Button>
          <Button onClick={() => onConfirm(idemKey)} disabled={pending || !config}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("ops.subscribe.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({
  open,
  onOpenChange,
  name,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  pending: boolean;
  onConfirm: (mode: CancelSubscriptionMode) => void;
}) {
  const { t } = useTranslation("billing");
  const [mode, setMode] = React.useState<CancelSubscriptionMode>("period_end");
  const [typed, setTyped] = React.useState("");
  const idPrefix = React.useId();

  React.useEffect(() => {
    if (open) {
      setMode("period_end");
      setTyped("");
    }
  }, [open]);

  const matches = typed === name;
  const modes: CancelSubscriptionMode[] = ["period_end", "immediate"];

  return (
    <Dialog open={open} onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent closeLabel={t("ops.close")}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (matches && !pending) onConfirm(mode);
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("ops.cancel.title", { name })}</DialogTitle>
            <DialogDescription>{t("ops.cancel.description")}</DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t("ops.cancel.whenLegend")}</legend>
            {modes.map((m) => (
              <label key={m} htmlFor={`${idPrefix}-${m}`} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  id={`${idPrefix}-${m}`}
                  type="radio"
                  name={`${idPrefix}-mode`}
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">{t(`ops.cancel.modes.${m}.label`)}</span>
                  <span className="block text-muted-foreground">{t(`ops.cancel.modes.${m}.hint`)}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-confirm`}>{t("ops.cancel.typeName", { name })}</Label>
            <Input
              id={`${idPrefix}-confirm`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={name}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("ops.cancelAction")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!matches || pending}>
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.cancel.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
